import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useAuth } from "../../contexts/AuthContext";
import { useSearchParams } from "react-router-dom";

const FormContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  color: #333;
  margin-bottom: 8px;
  font-size: 2rem;
  font-weight: 700;
  text-align: center;
`;

const Subtitle = styled.p`
  color: #666;
  margin-bottom: 32px;
  text-align: center;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const Label = styled.label`
  color: #374151;
  font-weight: 500;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const Button = styled.button<{ $variant?: "primary" | "secondary" }>`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  ${(props) =>
    props.$variant === "secondary"
      ? `
    background: #f3f4f6;
    color: #374151;
    
    &:hover {
      background: #e5e7eb;
    }
  `
      : `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-bottom: 16px;
`;

const SuccessMessage = styled.div`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #16a34a;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-bottom: 16px;
`;

const PasswordRequirements = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-top: 8px;
`;

const RequirementTitle = styled.h4`
  color: #374151;
  font-size: 0.9rem;
  margin: 0 0 8px 0;
  font-weight: 600;
`;

const RequirementList = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: #6b7280;
  font-size: 0.85rem;

  li {
    margin-bottom: 4px;
  }
`;

const WelcomeSection = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const UserEmail = styled.div`
  background: #f3f4f6;
  padding: 12px 16px;
  border-radius: 8px;
  color: #374151;
  font-weight: 500;
  margin-bottom: 16px;
`;

const OnboardingForm: React.FC = () => {
  const { completeOnboarding } = useAuth();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [userEmail, setUserEmail] = useState("");

  useEffect(() => {
    // Get email from URL parameters
    const email = searchParams.get("email");
    if (email) {
      setUserEmail(decodeURIComponent(email));
    }
  }, [searchParams]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
    setSuccess("");
  };

  const validatePassword = (password: string): boolean => {
    const requirements = [
      password.length >= 8,
      /(?=.*[a-z])/.test(password),
      /(?=.*[A-Z])/.test(password),
      /(?=.*\d)/.test(password),
      /(?=.*[@$!%*?&])/.test(password),
    ];
    return requirements.every((req) => req);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    // Validation
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError("Nome e cognome sono obbligatori");
      setLoading(false);
      return;
    }

    if (!validatePassword(formData.password)) {
      setError("La password non soddisfa i requisiti");
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Le password non corrispondono");
      setLoading(false);
      return;
    }

    const token = searchParams.get("token");
    if (!token) {
      setError("Link di benvenuto non valido. Contatta il tuo amministratore.");
      setLoading(false);
      return;
    }

    const result = await completeOnboarding({
      token,
      password: formData.password,
      firstName: formData.firstName,
      lastName: formData.lastName,
    });

    if (result.success) {
      setSuccess(
        "Configurazione account completata con successo! Ora sei loggato."
      );
    } else {
      setError(result.message || "Impossibile completare la configurazione dell'account");
    }

    setLoading(false);
  };

  return (
    <FormContainer>
      <Title>Completa il Tuo Account</Title>
      <Subtitle>
        Benvenuto in Lead Me! Completa la configurazione del tuo account per iniziare.
      </Subtitle>

      <WelcomeSection>
        {userEmail && (
          <UserEmail>Configurazione account per: {userEmail}</UserEmail>
        )}
      </WelcomeSection>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      <Form onSubmit={handleSubmit}>
        <InputRow>
          <InputGroup>
            <Label htmlFor="firstName">Nome</Label>
            <Input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Mario"
              required
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="lastName">Cognome</Label>
            <Input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Rossi"
              required
            />
          </InputGroup>
        </InputRow>

        <InputGroup>
          <Label htmlFor="password">Crea Password</Label>
          <Input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Crea una password sicura"
            required
          />
          <PasswordRequirements>
            <RequirementTitle>Requisiti Password:</RequirementTitle>
            <RequirementList>
              <li>Almeno 8 caratteri</li>
              <li>Contiene almeno una lettera minuscola</li>
              <li>Contiene almeno una lettera maiuscola</li>
              <li>Contiene almeno un numero</li>
              <li>Contiene almeno un carattere speciale (@$!%*?&)</li>
            </RequirementList>
          </PasswordRequirements>
        </InputGroup>

        <InputGroup>
          <Label htmlFor="confirmPassword">Conferma Password</Label>
          <Input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="Conferma la tua password"
            required
          />
        </InputGroup>

        <Button type="submit" disabled={loading}>
          {loading ? "⏳ Configurazione account..." : "✅ Completa Configurazione"}
        </Button>
      </Form>
    </FormContainer>
  );
};

export default OnboardingForm;
